/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: CallFlowsFormView.tsx
 🗓️ Created: 2025-01-28
 🧩 Description:
    Form view for creating and editing Call Flows.
    Manages call flow configuration with General and Advanced Settings sections.

 🔧 Notes:
    - Domain-scoped data
    - Supports JSONB settings for advanced FreeSWITCH features
    - Dual destinations (enabled/disabled) based on status

 🔁 Last Updated: 2025-11-07 by GPT-5 Codex
────────────────────────────────────────────────────────────────*/

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { Copy, GitBranch, Plus, Search } from 'lucide-react';
import { ModuleFormHeader } from '../../components/layouts';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import FixedActionBar from '../../components/ui/FixedActionBar';
import { useSaveWithAction } from '../../hooks/useSaveWithAction';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../contexts/NotificationContext';
import { useNavigationStore } from '../../store/navigation';
import { deleteCallFlowById, navigateToCallFlowDuplicate } from './callFlowsActions';
import { QuickSearchModal } from '../../components/ui/QuickSearchModal';
import { fetchCallFlow, createCallFlow, updateCallFlow, fetchCallFlows } from './api/callFlowApi';
import { CallFlow, CallFlowFormState, CallFlowSettings } from './types';
import { CallFlowGeneralSection } from './components/CallFlowGeneralSection';
import { CallFlowSettingsSection } from './components/CallFlowSettingsSection';

const DEFAULT_SETTINGS: CallFlowSettings = {
  announcement_enabled: '',
  announcement_disabled: '',
  play_announcement: true,
  allow_feature_toggle: true,
  schedule_enabled: false,
  schedule: null,
  failover_module: '',
  failover_data: '',
  toggle_announcement: '',
  blf_behavior: 'sync',
  event_webhook: '',
  allowed_extensions: [],
  record_call: false,
  max_duration: null,
  time_condition_fallback: null,
};

const DEFAULT_FORM: CallFlowFormState = {
  name: '',
  extension: '',
  context: '',
  status: false,
  enabledDestinationModule: '',
  enabledDestinationData: '',
  disabledDestinationModule: '',
  disabledDestinationData: '',
  toggleEnabled: true,
  enabled: true,
  settings: DEFAULT_SETTINGS,
};

const deepClone = <T,>(value: T): T =>
  typeof structuredClone === 'function'
    ? structuredClone(value)
    : JSON.parse(JSON.stringify(value));

const generateContextFromName = (rawName: string) =>
  rawName
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .trim()
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '')
    .substring(0, 80)
    .replace(/^cf_/, '')
    .replace(/^/, 'cf_');

const normalizeResponse = (payload: CallFlow): CallFlowFormState => ({
  name: payload.name || '',
  extension: payload.extension || '',
  context: payload.context || '',
  status: Boolean(payload.status ?? false),
  enabledDestinationModule: payload.enabledDestinationModule || '',
  enabledDestinationData: payload.enabledDestinationData || '',
  disabledDestinationModule: payload.disabledDestinationModule || '',
  disabledDestinationData: payload.disabledDestinationData || '',
  toggleEnabled: Boolean(payload.toggleEnabled ?? true),
  enabled: Boolean(payload.enabled ?? true),
  settings: {
    ...DEFAULT_SETTINGS,
    ...(payload.settings ?? {}),
  },
});

export default function CallFlowsFormView() {
  const { t } = useI18n();
  const { showSuccess, showError, showInfo } = useNotifications();
  const { setCurrentPageTitle } = useNavigationStore();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const duplicateId = searchParams.get('duplicate');
  const resetParam = searchParams.get('_reset');
  const isEditMode = Boolean(id && id !== 'new');

  const [formData, setFormData] = useState<CallFlowFormState>(deepClone(DEFAULT_FORM));
  const [originalData, setOriginalData] = useState<CallFlowFormState>(deepClone(DEFAULT_FORM));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(isEditMode || Boolean(duplicateId));
  const [isSaving, setIsSaving] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showQuickList, setShowQuickList] = useState(false);
  const [quickListSearch, setQuickListSearch] = useState('');
  const [quickListItems, setQuickListItems] = useState<Array<{ id: number; name: string; description?: string | null }>>([]);
  const [isQuickListLoading, setIsQuickListLoading] = useState(false);
  const lastSaveTimestamp = useRef(0);
  const quickListLoadingRef = useRef(false);

  // Refs for state management
  const lastLoadedIdRef = useRef<string | null>(null);
  const isLoadingRef = useRef<boolean>(false);
  const currentIdRef = useRef<string | undefined>(undefined);
  const hasLoadedInitialDataRef = useRef<boolean>(false);
  const lastIdRef = useRef<string | undefined>(id);
  const lastResetParamRef = useRef<string | null>(null);

  // Stable refs for functions
  const showErrorRef = useRef(showError);
  const showSuccessRef = useRef(showSuccess);
  const showInfoRef = useRef(showInfo);
  const tRef = useRef(t);
  const navigateRef = useRef(navigate);

  useEffect(() => {
    showErrorRef.current = showError;
    showSuccessRef.current = showSuccess;
    showInfoRef.current = showInfo;
    tRef.current = t;
    navigateRef.current = navigate;
  }, [showError, showSuccess, showInfo, t, navigate]);

  // Reset form when navigating to 'new' after Save and New
  useEffect(() => {
    const currentId = id || 'new';
    const previousId = lastIdRef.current;
    const currentResetParam = resetParam;

    const shouldReset = (!id || id === 'new') &&
      !isEditMode &&
      (previousId !== currentId ||
        (currentResetParam && currentResetParam !== lastResetParamRef.current));

    if (shouldReset) {
      setFormData(deepClone(DEFAULT_FORM));
      setOriginalData(deepClone(DEFAULT_FORM));
      setErrors({});

      if (currentResetParam) {
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete('_reset');
        const newSearch = newSearchParams.toString();
        window.history.replaceState({}, '', `${window.location.pathname}${newSearch ? `?${newSearch}` : ''}`);
      }
    }

    lastIdRef.current = currentId;
    lastResetParamRef.current = currentResetParam;
  }, [id, isEditMode, resetParam, searchParams]);

  const pageTitle = useMemo(
    () => (isEditMode ? `${t('callFlows.editEntry')} - ${formData.name || ''}` : t('callFlows.createEntry')),
    [isEditMode, t, formData.name],
  );

  const applyLoadedData = useCallback(
    (data: CallFlowFormState, { duplicate }: { duplicate: boolean }) => {
      const merged = deepClone(data);
      if (!merged.context) {
        merged.context = generateContextFromName(merged.name);
      }
      if (duplicate) {
        const copySuffix = tRef.current('common:copySuffix');
        merged.name = `${merged.name} ${copySuffix}`.trim();
        merged.context = generateContextFromName(merged.name);
        setTimeout(() => {
          setOriginalData(deepClone(DEFAULT_FORM));
          setFormData(deepClone(merged));
        }, 0);
      } else {
        setTimeout(() => {
          setOriginalData(deepClone(merged));
          setFormData(deepClone(merged));
        }, 0);
      }
    },
    [],
  );

  const applyLoadedDataRef = useRef(applyLoadedData);
  useEffect(() => {
    applyLoadedDataRef.current = applyLoadedData;
  }, [applyLoadedData]);

  const loadCallFlowFn = useCallback(
    async (callFlowId: string, { duplicate }: { duplicate: boolean }) => {
      if (isLoadingRef.current || lastLoadedIdRef.current === callFlowId) {
        return;
      }
      isLoadingRef.current = true;
      lastLoadedIdRef.current = callFlowId;
      setIsLoading(true);
      try {
        const data = await fetchCallFlow(callFlowId);
        applyLoadedDataRef.current(normalizeResponse(data), { duplicate });
      } catch (error) {
        lastLoadedIdRef.current = null;
        showErrorRef.current(tRef.current('callFlows.messages.loadError'));
        navigateRef.current('/pbx/incoming-tools/call-flows');
      } finally {
        isLoadingRef.current = false;
        setIsLoading(false);
      }
    },
    [],
  );

  const loadCallFlowRef = useRef(loadCallFlowFn);
  useEffect(() => {
    loadCallFlowRef.current = loadCallFlowFn;
  }, [loadCallFlowFn]);

  useEffect(() => {
    const loadId = duplicateId || id;
    const idChanged = currentIdRef.current !== loadId;

    if (idChanged) {
      currentIdRef.current = loadId;
    }

    if (isEditMode && id && idChanged) {
      setTimeout(() => {
        loadCallFlowRef.current(id, { duplicate: false });
      }, 0);
    } else if (duplicateId && idChanged) {
      setTimeout(() => {
        loadCallFlowRef.current(duplicateId, { duplicate: true });
      }, 0);
    } else if (!isEditMode && !duplicateId) {
      setTimeout(() => {
        lastLoadedIdRef.current = null;
      }, 0);
    }
  }, [duplicateId, id, isEditMode]);

  // Reset load guard on unmount
  useEffect(() => {
    return () => {
      lastLoadedIdRef.current = null;
      currentIdRef.current = undefined;
    };
  }, []);

  useEffect(() => {
    if (!isEditMode && !duplicateId && formData.name.trim().length > 0 && !isLoadingRef.current) {
      setFormData((prev) => ({
        ...prev,
        context: generateContextFromName(prev.name),
      }));
    }
  }, [duplicateId, formData.name, isEditMode]);

  const handleFieldChange = <K extends keyof CallFlowFormState>(
    field: K,
    value: CallFlowFormState[K],
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field as string]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field as string];
        return next;
      });
    }
  };

  const handleSettingChange = <K extends keyof CallFlowSettings>(
    field: K,
    value: CallFlowSettings[K],
  ) => {
    setFormData((prev) => ({
      ...prev,
      settings: {
        ...prev.settings,
        [field]: value,
      },
    }));
  };

  const hasChanges = useMemo(() => {
    return JSON.stringify(formData) !== JSON.stringify(originalData);
  }, [formData, originalData]);

  const validateForm = useCallback(() => {
    const nextErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      nextErrors.name = t('callFlows.validation.nameRequired');
    }
    if (!formData.enabledDestinationModule.trim()) {
      nextErrors.enabledDestinationModule = t('callFlows.validation.enabledDestinationRequired');
    }
    if (!formData.enabledDestinationData.trim()) {
      nextErrors.enabledDestinationData = t('callFlows.validation.enabledDestinationDataRequired');
    }
    if (!formData.disabledDestinationModule.trim()) {
      nextErrors.disabledDestinationModule = t('callFlows.validation.disabledDestinationRequired');
    }
    if (!formData.disabledDestinationData.trim()) {
      nextErrors.disabledDestinationData = t('callFlows.validation.disabledDestinationDataRequired');
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }, [formData, t]);

  const handleSubmit = useCallback(
    async (event?: React.FormEvent): Promise<CallFlow | undefined> => {
      event?.preventDefault();

      const now = Date.now();
      if (now - lastSaveTimestamp.current < 1000) {
        return undefined;
      }
      lastSaveTimestamp.current = now;

      if (!validateForm()) {
        showErrorRef.current(tRef.current('common.validationError'));
        return undefined;
      }
      setIsSaving(true);
      try {
        const payload = {
          name: formData.name.trim(),
          extension: formData.extension.trim() || null,
          context: formData.context.trim() || generateContextFromName(formData.name),
          status: formData.status,
          enabledDestinationModule: formData.enabledDestinationModule.trim(),
          enabledDestinationData: formData.enabledDestinationData.trim(),
          disabledDestinationModule: formData.disabledDestinationModule.trim(),
          disabledDestinationData: formData.disabledDestinationData.trim(),
          toggleEnabled: formData.toggleEnabled,
          enabled: formData.enabled,
          settings: formData.settings,
        };

        const result = isEditMode
          ? await updateCallFlow(id!, payload)
          : await createCallFlow(payload);

        showSuccessRef.current(
          isEditMode
            ? tRef.current('callFlows.messages.updateSuccess')
            : tRef.current('callFlows.messages.createSuccess'),
        );

        setOriginalData(deepClone(formData));

        return result;
      } catch (error) {
        showErrorRef.current((error as Error).message || tRef.current('callFlows.messages.createError'));
        return undefined;
      } finally {
        setIsSaving(false);
      }
    },
    [formData, hasChanges, id, isEditMode, validateForm],
  );

  const handleSaveInternal = useCallback(async () => {
    const savedData = await handleSubmit();

    if (!savedData && isEditMode && id) {
      return { id: Number(id), uuid: String(id) } as CallFlow;
    }

    return savedData;
  }, [handleSubmit, isEditMode, id]);

  const handleSaveWithAction = useSaveWithAction({
    onSave: handleSaveInternal,
    listPath: '/pbx/incoming-tools/call-flows',
    newPath: '/pbx/incoming-tools/call-flows/new',
    mode: isEditMode ? 'edit' : 'create',
    getEditPath: (savedData) => {
      return `/pbx/incoming-tools/call-flows/${savedData.id || savedData.uuid}`;
    },
    getFormData: () => ({
      formData
    }),
    storageKey: 'callFlows:saveActionPreference'
  });

  const handleReset = () => {
    if (isEditMode) {
      setFormData(deepClone(originalData));
      setErrors({});
    } else {
      setFormData(deepClone(DEFAULT_FORM));
      setErrors({});
    }
  };

  const handleCancel = () => {
    if (isEditMode) {
      handleReset();
    } else {
      navigateRef.current('/pbx/incoming-tools/call-flows');
    }
  };

  const handleDeleteConfirm = useCallback(async () => {
    if (!isEditMode || !id) return;

    setIsDeleting(true);
    try {
      await deleteCallFlowById(id);
      showSuccessRef.current(tRef.current('callFlows.messages.deleteSuccess'));
      navigateRef.current('/pbx/incoming-tools/call-flows');
    } catch (error) {
      showErrorRef.current((error as Error).message || tRef.current('callFlows.messages.deleteError'));
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  }, [id, isEditMode]);

  const handleDuplicateFromForm = useCallback(() => {
    if (!isEditMode || !id) return;
    navigateToCallFlowDuplicate(navigateRef.current, id);
  }, [id, isEditMode]);

  const loadQuickList = useCallback(async () => {
    if (quickListLoadingRef.current) {
      return;
    }

    quickListLoadingRef.current = true;
    setIsQuickListLoading(true);
    try {
      const result = await fetchCallFlows(
        {
          page: 1,
          limit: 100,
          sortBy: 'name',
          sortOrder: 'asc',
        },
        undefined,
        'name',
      );
      const items = Array.isArray(result?.data)
        ? (result.data as Array<{ id: number; name: string; description?: string | null }>)
        : [];
      setQuickListItems(items);
    } catch {
      showErrorRef.current(tRef.current('callFlows.messages.loadError'));
      setQuickListItems([]);
    } finally {
      setIsQuickListLoading(false);
      quickListLoadingRef.current = false;
    }
  }, []);

  const filteredQuickList = useMemo(() => {
    const query = quickListSearch.trim().toLowerCase();
    if (!query) {
      return quickListItems;
    }

    return quickListItems.filter((item) =>
      item.name.toLowerCase().includes(query) ||
      (item.description ?? '').toLowerCase().includes(query)
    );
  }, [quickListItems, quickListSearch]);

  if (isLoading) {
    return (
      <div className="flex min-h-[16rem] items-center justify-center">
        <span className="text-muted-foreground">{t('common.loading')}</span>
      </div>
    );
  }

  return (
    <div className="p-6 pb-24">
      <ModuleFormHeader
        icon={<GitBranch className="h-5 w-5 text-primary" />}
        title={pageTitle}
        backButtonText={t('callFlows.actions.list')}
        onBack={() => navigateRef.current('/pbx/incoming-tools/call-flows')}
        actions={[
          {
            icon: <Search className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('callFlows.quickList.tooltip'),
            onClick: () => {
              setShowQuickList(true);
              void loadQuickList();
            },
          },
          ...(isEditMode && !duplicateId
            ? [{
              icon: <Copy className="h-5 w-5 text-muted-foreground" />,
              tooltip: t('common.duplicate'),
              onClick: handleDuplicateFromForm,
            }]
            : []),
          {
            icon: <Plus className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('callFlows.actions.add'),
            onClick: () => navigateRef.current('/pbx/incoming-tools/call-flows/new'),
          },
        ]}
      />

      <form onSubmit={handleSubmit} className="mt-8 space-y-6">
        <CallFlowGeneralSection
          formData={formData}
          errors={errors}
          onFieldChange={handleFieldChange}
          onNameChange={(name) => {
            handleFieldChange('name', name);
            handleFieldChange('context', generateContextFromName(name));
          }}
          t={t}
        />

        <CallFlowSettingsSection
          settings={formData.settings}
          onSettingChange={handleSettingChange}
          t={t}
        />

        <FixedActionBar
          onSaveWithAction={handleSaveWithAction}
          onCancel={handleCancel}
          onDelete={isEditMode ? () => setShowDeleteModal(true) : undefined}
          isEditMode={isEditMode}
          isSaving={isSaving}
          isLoading={isLoading}
          saveText={t('callFlows.actions.save')}
          cancelText={t('callFlows.actions.cancel')}
          deleteText={t('callFlows.actions.delete')}
          useSaveDropdown={true}
          saveDropdownStorageKey="callFlows:saveActionPreference"
        />
      </form>

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title={t('callFlows.messages.deleteTitle')}
        message={t('callFlows.messages.confirmDelete', { name: formData.name || '' })}
        confirmText={t('common:delete')}
        cancelText={t('common:cancel')}
        type="danger"
        isLoading={isDeleting}
      />

      <QuickSearchModal
        isOpen={showQuickList}
        onClose={() => {
          setShowQuickList(false);
          setQuickListSearch('');
          setIsQuickListLoading(false);
          quickListLoadingRef.current = false;
        }}
        title={t('callFlows.quickList.title')}
        items={filteredQuickList}
        isLoadingInitial={isQuickListLoading}
        isSearching={false}
        searchValue={quickListSearch}
        onSearchChange={setQuickListSearch}
        onItemSelect={(item) => {
          setShowQuickList(false);
          setQuickListSearch('');
          navigateRef.current(`/pbx/incoming-tools/call-flows/${item.id}`, { replace: true });
        }}
        currentItemId={id}
        renderItem={(item, isCurrent) => (
          <button
            type="button"
            disabled={isCurrent}
            onClick={() => {
              if (isCurrent) return;
              setShowQuickList(false);
              setQuickListSearch('');
              navigateRef.current(`/pbx/incoming-tools/call-flows/${item.id}`, { replace: true });
            }}
            className={`w-full text-left p-3 rounded-md border border-border transition-colors ${isCurrent
              ? 'bg-primary/20 border-2 border-primary cursor-default opacity-60'
              : 'hover:bg-muted/40'
              }`}
          >
            <div className="font-medium text-foreground">
              {item.name}
            </div>
            {item.description && (
              <div className="text-xs text-muted-foreground mt-0.5">
                {item.description}
              </div>
            )}
          </button>
        )}
        searchPlaceholder={t('callFlows.quickList.searchPlaceholder')}
        emptyMessage={t('callFlows.quickList.empty')}
        headerIcon={<GitBranch className="h-5 w-5" />}
        minSearchChars={0}
      />
    </div>
  );
}
